export const TEST_QR_SCAN_BASE_URL = 'https://scan.ocmenafestival.com/scan?code=';

export const buildTestQrValue = (code, scanEventDate) => {
  const base = `${TEST_QR_SCAN_BASE_URL}${encodeURIComponent(code)}`;
  return scanEventDate ? `${base}&sed=${encodeURIComponent(scanEventDate)}` : base;
};

export const buildTestQrImageUrl = (code, size = 220, scanEventDate) => {
  const qrValue = buildTestQrValue(code, scanEventDate);
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=10&data=${encodeURIComponent(qrValue)}`;
};

// Festival days selectable on the qr-test page to simulate "today".
export const FESTIVAL_DAY_OPTIONS = [
  { date: '2026-06-19', label: 'Fri Jun 19' },
  { date: '2026-06-20', label: 'Sat Jun 20' },
  { date: '2026-06-21', label: 'Sun Jun 21' },
];

export const ADMIN_CORE_QR_SCENARIOS = [
  {
    code: 'TESTQR-PAC-AMP-SAT',
    label: 'PAC AMP SAT',
    expected: {
      type: 'success',
      status: 'VALID → SUCCESS',
      title: 'PAC AMP Saturday check-in success',
      message: 'Shows amphitheater success card with section/row/seat and Saturday event date.',
    },
  },
  {
    code: 'TESTQR-PAC-AMP-SUN',
    label: 'PAC AMP SUN',
    expected: {
      type: 'success',
      status: 'VALID → SUCCESS',
      title: 'PAC AMP Sunday check-in success',
      message: 'Shows amphitheater success card with section/row/seat and Sunday event date.',
    },
  },
  {
    code: 'TESTQR-FESTIVAL-VALID',
    label: '3-Day Festival Pass',
    expected: {
      type: 'success',
      status: 'VALID → SUCCESS',
      title: 'Festival pass success',
      message: 'Shows festival success screen (range June 19-21) with pass details.',
    },
  },
  {
    code: 'TESTQR-FESTIVAL-1DAY',
    label: '1-Day Festival Pass',
    expected: {
      type: 'success',
      status: 'VALID → SUCCESS',
      title: '1-Day festival pass success',
      message: 'Always valid on every scan. Single-QR 1-day festival pass demo.',
    },
  },
  {
    code: 'TESTQR-FESTIVAL-2DAY',
    label: '2-Day Festival Pass',
    expected: {
      type: 'success',
      status: 'VALID → SUCCESS',
      title: '2-Day festival pass success',
      message: 'Always valid on every scan. Single-QR 2-day festival pass demo.',
    },
  },
  {
    code: 'TESTQR-VENDOR-VALID',
    label: 'Vendor',
    expected: {
      type: 'success',
      status: 'VALID → SUCCESS',
      title: 'Vendor entry success',
      message: 'Shows vendor access success with business/booth details.',
    },
  },
  {
    code: 'TESTQR-VENDOR-COMP-FEST',
    label: 'Vendor Comps Festival Passes',
    expected: {
      type: 'success',
      status: 'VALID → SUCCESS',
      title: 'Vendor comp festival pass success',
      message: 'Shows complimentary/vendor-comp style festival success result.',
    },
  },
  {
    code: 'TESTQR-PARKING-VALID-ADMIN',
    label: 'Parking Passes',
    expected: {
      type: 'success',
      status: 'VALID → SUCCESS',
      title: 'VIP parking pass success',
      message: 'Always valid on every scan (admin training parking demo).',
    },
  },
  {
    code: 'TESTQR-VOIDED-REFUNDED',
    label: 'Voided / Refunded',
    expected: {
      type: 'error',
      status: 'VOIDED',
      title: 'Blocked: voided/refunded ticket',
      message: 'Scanner blocks entry and shows a red invalid/voided message.',
    },
  },
];

export const ADMIN_EXTRA_QR_SCENARIOS = [
  {
    code: 'TESTQR-ALREADY-USED',
    label: 'Already Scanned Ticket',
    expected: {
      type: 'used',
      status: 'ALREADY_USED',
      title: 'Already used result',
      message: 'Always shows the already-used screen with used timestamp details.',
    },
  },
  {
    code: 'TESTQR-FLEX-SAME-DAY-BLOCK',
    label: '2-Day Pass Same-Day Duplicate',
    expected: {
      type: 'used',
      status: 'ALREADY_USED',
      title: 'Same-day duplicate blocked',
      message: 'Shows same-group same-day block message for multi-day pass behavior.',
    },
  },
  {
    code: 'TESTQR-WRONG-DAY',
    label: 'Wrong Day Attempt',
    expected: {
      type: 'error',
      status: 'WRONG_DAY',
      title: 'Wrong date block',
      message: 'Scanner blocks and shows valid event day guidance.',
    },
  },
];

const buildLoadRow = (code, seatLabel) => ({ code, seatLabel });

export const PUBLIC_QR_TEST_GROUPS = [
  {
    key: 'fest-3day',
    title: '3-Day Festival Pass',
    subtitle: 'One pass, 3 wristbands — only ONE scans per festival day',
    multiDay: true,
    rows: [
      buildLoadRow('TESTQR-LOAD-3DAY-Q1', 'Wristband 1 of 3'),
      buildLoadRow('TESTQR-LOAD-3DAY-Q2', 'Wristband 2 of 3'),
      buildLoadRow('TESTQR-LOAD-3DAY-Q3', 'Wristband 3 of 3'),
    ],
  },
  {
    key: 'fest-2day',
    title: '2-Day Festival Pass',
    subtitle: 'One pass, 2 wristbands — only ONE scans per festival day',
    multiDay: true,
    rows: [
      buildLoadRow('TESTQR-LOAD-2DAY-Q1', 'Wristband 1 of 2'),
      buildLoadRow('TESTQR-LOAD-2DAY-Q2', 'Wristband 2 of 2'),
    ],
  },
  {
    key: 'fest-1day',
    title: '1-Day Festival Pass',
    subtitle: 'One pass, 1 wristband — valid on any single festival day',
    multiDay: true,
    rows: [
      buildLoadRow('TESTQR-LOAD-1DAY-Q1', 'Wristband 1 of 1'),
    ],
  },
  {
    key: 'amp-sat',
    title: 'PAC AMP SAT (5)',
    subtitle: 'Saturday amphitheater simulation',
    rows: [
      buildLoadRow('TESTQR-LOAD-AMP-SAT-01', 'PIT / Row AAAA / Seat 21'),
      buildLoadRow('TESTQR-LOAD-AMP-SAT-02', 'PIT / Row AAAA / Seat 22'),
      buildLoadRow('TESTQR-LOAD-AMP-SAT-03', 'PIT / Row AAAA / Seat 23'),
      buildLoadRow('TESTQR-LOAD-AMP-SAT-04', 'PIT / Row AAAA / Seat 24'),
      buildLoadRow('TESTQR-LOAD-AMP-SAT-05', 'PIT / Row AAAA / Seat 25'),
    ],
  },
  {
    key: 'amp-sun',
    title: 'PAC AMP SUN (5)',
    subtitle: 'Sunday amphitheater simulation',
    rows: [
      buildLoadRow('TESTQR-LOAD-AMP-SUN-01', 'Section 6 / Row M / Seat 11'),
      buildLoadRow('TESTQR-LOAD-AMP-SUN-02', 'Section 6 / Row M / Seat 12'),
      buildLoadRow('TESTQR-LOAD-AMP-SUN-03', 'Section 6 / Row M / Seat 13'),
      buildLoadRow('TESTQR-LOAD-AMP-SUN-04', 'Section 6 / Row M / Seat 14'),
      buildLoadRow('TESTQR-LOAD-AMP-SUN-05', 'Section 6 / Row M / Seat 15'),
    ],
  },
  {
    key: 'parking',
    title: 'Parking Passes (1)',
    subtitle: 'VIP parking entry simulation',
    rows: [
      buildLoadRow('TESTQR-PARKING-VALID', 'VIP Parking Pass Demo'),
    ],
  },
];
